/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import { Scenario }                from '@/models/scenario/scenario';
import { DecisionPoint }           from '@/models/scenario/decision.point';
import { WorkBloxEditor }          from '@/screens/ide/workblox/work.blox.editor';

import * as $ from 'jquery';

import 'select2';
import {
  OptGroupData,
  OptionData,
  SearchOptions
} from 'select2';
import KeyDownEvent = JQuery.KeyDownEvent;
import KeyUpEvent   = JQuery.KeyUpEvent;
import {
  ICellEditorComp,
  ICellEditorParams
} from "ag-grid-community";

/**
 *
 * @class WorkDecisionPointEditor
 *
 * @brief dynamic dropdown list for decision points, also allows for type ahead on decision
 * point name.
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class WorkDecisionPointEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the selectable values (decision points) */
  public values: DecisionPoint[] = [];

  /** the selected milepost */
  public value: DecisionPoint = null;

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListener: (e: MouseEvent) => void = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    const de         = this;
    de.editingDone   = params.stopEditing;

    de.value         = null;
    de.values        = [];

    /* pre-select value if there is an existing decision point selected already */

    if (params.value instanceof DecisionPoint) {

      de.value = params.value;
    }

    /* try to figure out what mileposts we can even select from */

    const editor: WorkBloxEditor = params.context?.workblox;

    if (editor) {

      const scenario: Scenario = editor.getScenario();

      if (scenario) {

        de.values = scenario.milePoints();

      }
    }

    /* set up the Select2 drop down ... */

    de.gui   = document.createElement('div');
    de.gui.tabIndex = 0;

    if (!de.values || (de.values.length === 0)) {

      /* nothing to do, we don't have any decision points */
      return ;
    }

    const data: any[] = [];

    /* walk the decision points, generating a select option for each */

    for (const dp of de.values) {

      /* build this option ... */

      const name: string    = dp._name.trim().toUpperCase();
      const mile: string    = dp._mileage.toFixed(2);
      let selected: boolean = false;

      /* is this the current value? */

      if (de.value instanceof DecisionPoint) {

        selected = dp._id === de.value._id;
      }

      /* add the option */

      data.push({
        id: `dp-${dp._id}`,
        text: `${name} - ${mile} mi`,
        name,
        dp,
        selected
      });

    }

    /* insert new HTML for our custom select ... */

    const html = `<select class="work-blox-dp-select"><option></option></select>`;
    $(de.gui).html(html);

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    const el: JQuery = $(de.gui).find('select.work-blox-dp-select');

    $(el).on('select2:close', () => {

      $(el).trigger('focus');
    });

    $(el).on("select2:closed", function() {

      $(el).select2("open");
    });

    $(el).on('select2:select', (e) => {

      const   selBox: JQuery =  $('input.select2-search__field');

      $(selBox).trigger('focus');

      const datum: any           = e.params.data;

      /* update the current value */

      de.value = datum.dp;

      de.editingDone(true);
    });

    $(el).select2({

      placeholder:  `Select...`,
      allowClear:    false,
      closeOnSelect: true,
      width:         '200px',

      data,

      /* our type-ahead matching function */
      matcher: (args: SearchOptions, datum: OptGroupData | OptionData): any => {

        /* do we have something to match? */

        if ((args?.term ?? '').trim() === '') {

          return datum;
        }

        // @ts-ignore
        const name: string = datum?.name;

        if (!name) {

          /* likely the placeholder option */

          return null;
        }

        if (name.toUpperCase().indexOf(args.term.toUpperCase()) === 0) {

          return datum;
        }

        return null;
      }

    });

    $(el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).css('width', 'auto');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

      $(selBox).trigger('focus');

    });

    setTimeout(() => {

      $(el).select2('open');

      $(de.gui).on('keyup', (e: KeyUpEvent) => {

        if (e.key !== 'Tab') {

          return ;
        }

        de.editingDone(true);

      });

      $(de.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        de.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    de.clickListener = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === de.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', de.clickListener);

      de.editingDone(true);
    };

    document.addEventListener('click', de.clickListener);
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): DecisionPoint {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListener);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.work-blox-dp-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    /* if there are no decision points...nothing we can do */

    return this.values.length === 0;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
